import { useState, useEffect, useRef, useCallback } from "react";
import { Head } from "zudoku/components";
import { ArrowRight, Search } from "lucide-react";
import blogPosts from "../config/blogPosts";
import { authorFor, authorPhoto } from "../config/authors";
import { SITE, BRAND_IMAGE, FEED_PATH } from "./brand";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

/**
 * Blog index — the /pub landing page.
 *
 * Section grammar follows the V3 blog design (
 * `app/src/components/blog/BlogPage.tsx`, IndexView): a mint hero band with
 * a forest kicker rule, then a white body holding a sharp-edged featured card
 * and a three-up card grid. Kicker/headline/subcopy are the design's own
 * (`content/blog.ts` → blogV3).
 *
 * What is ours and not the design's: the Pagefind search. The marketing blog has
 * no search backend; this site does, and it is the reason the page exists. It is
 * styled to the design's input token (8px radius, sage border) rather than
 * invented from scratch.
 *
 * Posts come from config/blogPosts.json, which sync_content.py generates from
 * the S3 bucket — this component never hardcodes editorial content.
 */

const BASE = "/pub";

/* ── Pagefind search hook ───────────────────────────────────────────── */

interface SearchResult {
  url: string;
  title: string;
  excerpt: string;
}

function usePagefindSearch() {
  const pagefindRef = useRef<any>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const initPagefind = useCallback(async () => {
    if (pagefindRef.current) return;
    try {
      pagefindRef.current = await import(
        /* @vite-ignore */ `${BASE}/pagefind/pagefind.js`
      );
      await pagefindRef.current.options({ basePath: `${BASE}/pagefind/` });
    } catch {
      // Pagefind not available (e.g. dev mode) — search degrades gracefully
    }
  }, []);

  const search = useCallback(
    (query: string) => {
      clearTimeout(debounceRef.current);

      if (!query.trim()) {
        setResults([]);
        setSearching(false);
        return;
      }

      setSearching(true);

      debounceRef.current = setTimeout(async () => {
        await initPagefind();
        const pf = pagefindRef.current;
        if (!pf) {
          setSearching(false);
          return;
        }

        try {
          const response = await pf.search(query);
          const loaded: SearchResult[] = await Promise.all(
            response.results.slice(0, 10).map(async (r: any) => {
              const data = await r.data();
              return {
                url: data.url,
                title: data.meta?.title || data.url,
                excerpt: data.excerpt,
              };
            }),
          );
          setResults(loaded);
        } catch {
          setResults([]);
        }
        setSearching(false);
      }, 250);
    },
    [initPagefind],
  );

  return { search, results, searching };
}

/* ── Component ──────────────────────────────────────────────────────── */

export const BlogHome = () => {
  const [query, setQuery] = useState("");
  const { search, results, searching } = usePagefindSearch();

  useEffect(() => {
    search(query);
  }, [query, search]);

  const isSearching = query.trim().length > 0;
  const [featured, ...rest] = blogPosts;

  return (
    <>
      {/*
        This page is a `custom-page` with layout:"none" (config/navigation.tsx),
        so it renders OUTSIDE Zudoku's Layout — and therefore outside the
        "layout-after-head" slot that carries components/SeoHead.tsx. Every blog
        POST gets its social and JSON-LD tags from there; the index carries its own.
        <title> still goes through metadata.title's "%s | HRMLESS" template, and
        the canonical still comes from canonicalUrlOrigin, so neither is repeated
        here — only the tags Zudoku has no config field for.
      */}
      <Head>
        <title>The HRMLESS Blog</title>
        <meta
          name="description"
          content="Ideas for teams that hire at volume — practical guides on AI screening, interview automation and applicant tracking from HRMLESS."
        />
      <link
        rel="alternate"
        type="application/atom+xml"
        title="The HRMLESS Blog"
        href={`${SITE}${FEED_PATH}`}
      />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HRMLESS" />
        <meta property="og:title" content="The HRMLESS Blog" />
        <meta
          property="og:description"
          content="Ideas for teams that hire at volume — practical guides on AI screening, interview automation and applicant tracking from HRMLESS."
        />
        <meta property="og:url" content={`${SITE}/pub/`} />
        <meta property="og:image" content={`${SITE}${BRAND_IMAGE}`} />
        <meta name="twitter:image" content={`${SITE}${BRAND_IMAGE}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "The HRMLESS Blog",
            url: `${SITE}/pub/`,
            publisher: { "@type": "Organization", name: "HRMLESS", url: SITE },
          })}
        </script>
      </Head>
      <style>{styles}</style>

      <SiteHeader />
      <div className="bh">
        {/* ── Hero (mint) ──────────────────────────────────────── */}
        <section className="bh-hero">
          <div className="bh-container bh-hero__inner">
            <p className="bh-kicker">
              <span className="bh-kicker__rule" />
              The HRMLESS Blog
            </p>
            <h1 className="bh-hero__title">Ideas for teams that hire at volume.</h1>
            <p className="bh-hero__sub">
              Practical thinking on structured interviews, voice AI, and defensible
              hiring from the HRMLESS team.
            </p>
            <div className="bh-search">
              <Search className="bh-search__icon" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search the blog and docs"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="bh-search__input"
                aria-label="Search the blog and docs"
              />
            </div>
          </div>
        </section>

        {/* ── Search results (shown when a query is active) ─────── */}
        {isSearching && (
          <section className="bh-body">
            <div className="bh-container">
              {searching && <p className="bh-status">Searching…</p>}

              {!searching && results.length > 0 && (
                <div className="bh-results">
                  {results.map((r) => (
                    <a key={r.url} href={r.url} className="bh-result">
                      <h3 className="bh-result__title">{r.title}</h3>
                      <p
                        className="bh-result__excerpt"
                        dangerouslySetInnerHTML={{ __html: r.excerpt }}
                      />
                    </a>
                  ))}
                </div>
              )}

              {!searching && results.length === 0 && (
                <div className="bh-empty">
                  <p className="bh-status">No results for &ldquo;{query}&rdquo;</p>
                  <button className="bh-clear" onClick={() => setQuery("")}>
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Post index (shown when no search query) ───────────── */}
        {!isSearching && (
          <section className="bh-body">
            <div className="bh-container bh-stack">
              {/* Featured — the most recent post */}
              {featured && (
                <a href={`${BASE}/blogs/${featured.slug}`} className="bh-featured">
                  <div className="bh-featured__media">
                    <img src={featured.image} alt="" loading="lazy" />
                  </div>
                  <div className="bh-featured__body">
                    <div className="bh-meta">
                      <span className="bh-chip">Featured</span>
                      <span className="bh-date">{featured.date}</span>
                    </div>
                    <h2 className="bh-featured__title">{featured.title}</h2>
                    {/* Author, NOT a link: the whole card is already one <a>,
                        and an <a> inside an <a> is invalid HTML that browsers
                        silently un-nest, breaking both. The byline on the post
                        page carries the link to /about/<slug>. */}
                    <span className="bh-author">
                      <img
                        className="bh-author__avatar"
                        src={authorPhoto(authorFor((featured as { author?: string }).author))}
                        alt=""
                        width={28}
                        height={28}
                        loading="lazy"
                      />
                      {authorFor((featured as { author?: string }).author).name}
                    </span>
                    <p className="bh-featured__teaser">{featured.description}</p>
                    <span className="bh-readmore">
                      Read article <ArrowRight className="bh-readmore__icon" aria-hidden="true" />
                    </span>
                  </div>
                </a>
              )}

              {rest.length > 0 && (
                <>
                  <div className="bh-count">
                    {blogPosts.length} article{blogPosts.length === 1 ? "" : "s"}
                  </div>
                  <div className="bh-grid">
                    {rest.map((post) => (
                      <a key={post.slug} href={`${BASE}/blogs/${post.slug}`} className="bh-card">
                        <div className="bh-card__media">
                          <img src={post.image} alt="" loading="lazy" />
                        </div>
                        <div className="bh-card__body">
                          <span className="bh-date">{post.date}</span>
                          <h3 className="bh-card__title">{post.title}</h3>
                          <span className="bh-author">
                            <img
                              className="bh-author__avatar"
                              src={authorPhoto(authorFor((post as { author?: string }).author))}
                              alt=""
                              width={24}
                              height={24}
                              loading="lazy"
                            />
                            {authorFor((post as { author?: string }).author).name}
                          </span>
                          <p className="bh-card__teaser">{post.description}</p>
                          <span className="bh-readmore">
                            Read article <ArrowRight className="bh-readmore__icon" aria-hidden="true" />
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        )}
      </div>
      <SiteFooter />
    </>
  );
};

export default BlogHome;

/* ── Styles ─────────────────────────────────────────────────────────── */

const styles = `
/* Break out of Zudoku's max-width container to span the full viewport. */
.bh {
  width: 100vw;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  overflow-x: hidden;
  font-family: var(--hl-font-body);
  color: var(--hl-text-primary);
  /* Slide the whole page under the transparent top state of the sticky header
     so the mint hero runs to the top, as it does on the public site. The pull
     belongs here and not on .bh-hero: overflow-x above makes .bh a clipping
     box, so a child pulled above its top edge is cut off instead of showing.
     --hl-nav-h is measured and published by SiteHeader; the fallback is its
     resting height (30px logo + 32px padding + 1px border). */
  margin-top: calc(-1 * var(--hl-nav-h, 63px));
}
.bh * { box-sizing: border-box; }

.bh-container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Hero (mint band) ───────────────────────────────── */
.bh-hero {
  background: var(--hl-surface-mint);
  /* Top padding absorbs the nav the page is sitting under (see .bh). */
  padding: calc(80px + var(--hl-nav-h, 63px)) 0 80px;
}
.bh-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}
.bh-kicker {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--hl-green-700);
}
.bh-kicker__rule {
  display: inline-block;
  width: 24px;
  height: 2px;
  background: var(--hl-green-600);
}
.bh-hero__title {
  margin: 0;
  max-width: 720px;
  font-family: var(--hl-font-display);
  font-weight: 500;
  font-size: clamp(40px, 4.6vw, 60px);
  line-height: 1.08;
  letter-spacing: -1px;
  text-wrap: balance;
  color: var(--hl-text-primary);
}
.bh-hero__sub {
  margin: 0;
  max-width: 560px;
  font-size: 19px;
  line-height: 1.6;
  text-wrap: pretty;
  color: var(--hl-text-soft);
}

/* ── Search ─────────────────────────────────────────── */
.bh-search {
  position: relative;
  width: 100%;
  max-width: 480px;
  margin-top: 12px;
}
.bh-search__icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--hl-text-muted);
  pointer-events: none;
}
.bh-search__input {
  width: 100%;
  height: 52px;
  padding: 0 20px 0 46px;
  border: 1px solid var(--hl-border-sage);
  border-radius: 8px;
  background: var(--hl-surface-page);
  color: var(--hl-text-primary);
  font-family: var(--hl-font-ui);
  font-size: 16px;
  outline: none;
  transition: border-color var(--hl-duration-fast) ease,
              box-shadow var(--hl-duration-fast) ease;
}
.bh-search__input::placeholder { color: var(--hl-text-muted); }
.bh-search__input:focus {
  border-color: var(--hl-green-600);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hl-green-600) 20%, transparent);
}

/* ── Body ───────────────────────────────────────────── */
.bh-body {
  background: var(--hl-surface-page);
  padding: 56px 0 80px;
}
.bh-stack { display: flex; flex-direction: column; gap: 40px; }

.bh-status {
  margin: 0;
  font-size: 15px;
  color: var(--hl-text-muted);
}
.bh-count {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--hl-text-muted);
}

/* Shared card furniture. V3 outer cards are SHARP (radius 0) with a sage
   hairline; only the media inside a card is allowed a radius. */
.bh-meta { display: flex; align-items: center; gap: 12px; }
.bh-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--hl-surface-chip);
  color: var(--hl-green-700);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.bh-date { font-size: 13px; color: var(--hl-text-muted); }
.bh-author { display: inline-flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 13px; font-weight: 600; color: var(--hl-ink); }
.bh-author__avatar { width: 24px; height: 24px; border-radius: 6px; object-fit: cover; background: var(--hl-surface-mint-soft); flex: none; }
.bh-featured .bh-author__avatar { width: 28px; height: 28px; border-radius: 8px; }
.bh-readmore {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--hl-font-ui);
  font-size: 15px;
  font-weight: 600;
  color: var(--hl-green-700);
  transition: gap var(--hl-duration-fast) var(--hl-ease-out-expo);
}
.bh-readmore__icon { width: 16px; height: 16px; }

/* ── Featured card ──────────────────────────────────── */
.bh-featured {
  display: grid;
  grid-template-columns: 1fr;
  border: 1px solid var(--hl-border-sage);
  background: var(--hl-surface-page);
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  transition: box-shadow var(--hl-duration-fast) ease;
}
.bh-featured:hover { box-shadow: var(--hl-shadow-card); }
.bh-featured:hover .bh-readmore { gap: 12px; }
.bh-featured__media {
  background: var(--hl-ink);
  min-height: 260px;
  overflow: hidden;
}
.bh-featured__media img {
  width: 100%;
  height: 100%;
  min-height: 260px;
  object-fit: cover;
  display: block;
}
.bh-featured__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 32px;
}
.bh-featured__title {
  margin: 0;
  font-family: var(--hl-font-display);
  font-weight: 600;
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.5px;
  text-wrap: balance;
  color: var(--hl-text-primary);
}
.bh-featured__teaser {
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  text-wrap: pretty;
  color: var(--hl-text-soft);
}

/* ── Card grid ──────────────────────────────────────── */
.bh-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
.bh-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--hl-border-sage);
  background: var(--hl-surface-page);
  text-decoration: none;
  color: inherit;
  transition: box-shadow var(--hl-duration-fast) ease;
}
.bh-card:hover { box-shadow: var(--hl-shadow-card); }
.bh-card:hover .bh-readmore { gap: 12px; }
.bh-card__media {
  aspect-ratio: 16 / 10;
  background: var(--hl-surface-mint);
  overflow: hidden;
}
.bh-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.bh-card__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
  flex: 1;
}
.bh-card__title {
  margin: 0;
  font-family: var(--hl-font-display);
  font-weight: 600;
  font-size: 20px;
  line-height: 1.25;
  letter-spacing: -0.5px;
  color: var(--hl-text-primary);
}
.bh-card__teaser {
  margin: 0;
  flex: 1;
  font-size: 15px;
  line-height: 1.6;
  color: var(--hl-text-soft);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Search results ─────────────────────────────────── */
.bh-results { display: flex; flex-direction: column; gap: 16px; }
.bh-result {
  display: block;
  padding: 24px 28px;
  border: 1px solid var(--hl-border-sage);
  background: var(--hl-surface-page);
  text-decoration: none;
  color: inherit;
  transition: box-shadow var(--hl-duration-fast) ease;
}
.bh-result:hover { box-shadow: var(--hl-shadow-card); }
.bh-result__title {
  margin: 0 0 8px;
  font-family: var(--hl-font-display);
  font-weight: 600;
  font-size: 18px;
  line-height: 1.3;
  letter-spacing: -0.5px;
  color: var(--hl-text-primary);
}
.bh-result__excerpt {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--hl-text-soft);
}
.bh-result__excerpt mark {
  padding: 1px 3px;
  background: var(--hl-surface-chip);
  color: var(--hl-green-700);
}

.bh-empty { display: flex; flex-direction: column; align-items: flex-start; gap: 16px; }
.bh-clear {
  height: 40px;
  padding: 0 20px;
  border: 1px solid var(--hl-border-sage);
  border-radius: 999px;
  background: transparent;
  color: var(--hl-text-primary);
  font-family: var(--hl-font-ui);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color var(--hl-duration-fast) ease, border-color var(--hl-duration-fast) ease;
}
.bh-clear:hover { color: var(--hl-green-600); border-color: var(--hl-green-600); }

/* ── Responsive ─────────────────────────────────────── */
@media (min-width: 640px) {
  .bh-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 768px) {
  .bh-container { padding: 0 40px; }
  .bh-hero { padding: calc(96px + var(--hl-nav-h, 63px)) 0 96px; }
  .bh-body { padding: 72px 0 96px; }
  .bh-featured { grid-template-columns: repeat(2, 1fr); }
  .bh-featured__body { padding: 48px; }
  .bh-featured__title { font-size: 32px; }
}
@media (min-width: 1024px) {
  .bh-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 1720px) {
  .bh-container { max-width: 1540px; }
}
`;
